import {
	createStyles,
	Divider,
	Flex,
	Group,
	ScrollArea,
	Stack,
} from '@mantine/core';
import { UserAvatar } from '@repo/common/components/UserAvatar';
import { Button, IconButton, Text } from '@repo/foundations';
import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAIEmbedded } from '../../../api';
import type { IAiChat } from '../../../api/hooks/aiChats';
import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { MessageGroup } from '../../../features/AiChatPreviewModal/components/MessageGroup';
import { formatHoursToHm } from '../../../features/AiImpactDashboard/utils';
import { AiQualityBadgeController } from '../../../features/AiQualityBadge/AiQualityBadgeController';
import { AiChatDrawer403 } from './AiChatDrawer403';
import { AiChatDrawerSkeleton } from './AiChatDrawerSkeleton';
import { ResourceDisplay } from './ResourceDisplay';

interface AiChatDrawerContentProps {
	chatId: string | null;
	selectedChat: IAiChat | null;
	onClose: () => void;
}

const useStyles = createStyles((theme) => ({
	content: {
		flex: 1,
		overflowY: 'auto',
		padding: theme.spacing.xl,
	},
}));

const AiChatDrawerContentInternal = memo(
	({ chatId, selectedChat, onClose }: AiChatDrawerContentProps) => {
		const navigate = useNavigate();
		const { classes, theme } = useStyles();
		const { data: prompt, isLoading } = useAIEmbedded({
			id: chatId || '',
			options: {
				enabled: !!chatId,
				retry: false, // Don't retry if the prompt is not found so we show the error fallback quicker
			},
		});

		const prompts = useMemo(
			() => (prompt ? [prompt, ...(prompt.children ?? [])] : []),
			[prompt]
		);

		const chat = useMemo(() => {
			if (selectedChat) return selectedChat;
			if (!prompt) return null;

			// Collect sentiments from main prompt and children
			const sentiments: (string | null)[] = [];
			if (prompt.user_feedback_raw?.sentiment) {
				sentiments.push(prompt.user_feedback_raw.sentiment);
			}
			for (const child of prompt.children ?? []) {
				if (child.user_feedback_raw?.sentiment) {
					sentiments.push(child.user_feedback_raw.sentiment);
				}
			}

			return {
				id: prompt.id,
				title: prompt.title || null,
				prompt: prompt.prompt ? [prompt.prompt] : [],
				user_id: prompt.user?.id || '',
				user_name: prompt.user?.display_name || null,
				user_profile_picture: prompt.user?.profile_picture || null,
				created_at: prompt.created_at,
				updated_at: prompt.updated_at,
				status: prompt.status || '',
				messages_count: 0,
				queried_resources_count: 0,
				queried_resource_types: [],
				queried_resource_details: [],
				time_saved_hours: null,
				time_savings_breakdown: undefined,
				complexity: null,
				system_rating: null,
				sentiments,
				persona_id: prompt.persona?.id,
				persona_name: prompt.persona?.name,
				persona_avatar_url: prompt.persona?.avatar_url,
				score: null,
			};
		}, [selectedChat, prompt]);

		const visibleCount = Math.min(
			3,
			chat?.queried_resource_details?.length || 0
		);

		if (isLoading || !chat) {
			return <AiChatDrawerSkeleton />;
		}

		return (
			<Stack w="100%" h="100%" px="xl" py="sm">
				<Stack spacing="xs">
					<Group position="apart" mb="md">
						<Group spacing="sm">
							<Text
								truncate
								maw={theme.other.space[90]}
								size="sm"
								weight="bold"
							>
								{chat.title || chat.prompt?.[0] || ''}
							</Text>
						</Group>
						<Group align="center" spacing="xs">
							<Button
								variant="default"
								onClick={() => navigate(`/ai/${chat.id}`)}
								size="sm"
							>
								Go to chat
							</Button>
							<IconButton
								variant="tertiary"
								iconName="x"
								onClick={onClose}
								size="sm"
							/>
						</Group>
					</Group>
					<Stack spacing="sm">
						<Flex gap="xs">
							<Text
								w={theme.other.space[32]}
								size="sm"
								color="text/secondary/default"
							>
								Run by
							</Text>
							<Group align="center" spacing="xs">
								<UserAvatar
									size="xxs"
									user={{
										id: chat.user_id,
										display_name: chat.user_name || 'Unknown user',
										first_name: '',
										last_name: '',
										profile_picture: chat.user_profile_picture,
									}}
								/>
								<Text size="sm" color="text/primary/default">
									{chat.user_name || 'Unknown user'}
								</Text>
							</Group>
						</Flex>
						<Flex gap="xs">
							<Text
								w={theme.other.space[32]}
								size="sm"
								color="text/secondary/default"
							>
								Messages
							</Text>
							<Text size="sm" color="text/primary/default">
								{chat.messages_count || 1}
							</Text>
						</Flex>
						<Flex gap="xs">
							<Text
								miw={theme.other.space[32]}
								w={theme.other.space[32]}
								size="sm"
								color="text/secondary/default"
							>
								Queried Resources
							</Text>
							<ResourceDisplay
								resources={chat.queried_resource_details}
								visibleCount={visibleCount}
							/>
						</Flex>
						<Flex gap="xs">
							<Text
								w={theme.other.space[32]}
								size="sm"
								color="text/secondary/default"
							>
								Time Savings
							</Text>
							{chat.time_saved_hours ? (
								<Text size="sm" color="text/primary/default">
									{formatHoursToHm(chat.time_saved_hours)}
								</Text>
							) : (
								<Text size="sm" color="text/secondary/default">
									No time savings
								</Text>
							)}
						</Flex>

						<Flex gap="xs">
							<Text
								w={theme.other.space[32]}
								size="sm"
								color="text/secondary/default"
							>
								Complexity
							</Text>
							{chat.complexity ? (
								<Text size="sm" tt="capitalize" color="text/primary/default">
									{chat.complexity}
								</Text>
							) : (
								<Text size="sm" color="text/secondary/default">
									No complexity
								</Text>
							)}
						</Flex>
						<Flex gap="xs">
							<Text
								w={theme.other.space[32]}
								size="sm"
								color="text/secondary/default"
							>
								System Rating
							</Text>
							{chat.score ? (
								<AiQualityBadgeController score={chat.score} />
							) : (
								<Text size="sm" color="text/secondary/default">
									No rating
								</Text>
							)}
						</Flex>
					</Stack>
					<Divider mt="2xl" size="xs" />
				</Stack>
				<ScrollArea className={classes.content}>
					<Stack spacing="5xl">
						{prompts.filter(Boolean).map((item) => (
							<MessageGroup key={item.id} prompt={item} />
						))}
					</Stack>
				</ScrollArea>
			</Stack>
		);
	}
);

function ErrorFallback({ selectedChat, onClose }: AiChatDrawerContentProps) {
	return function () {
		return <AiChatDrawer403 selectedChat={selectedChat} onClose={onClose} />;
	};
}

export const AiChatDrawerContent = memo((props: AiChatDrawerContentProps) => (
	<ErrorBoundary fallback={ErrorFallback(props)}>
		<AiChatDrawerContentInternal {...props} />
	</ErrorBoundary>
));
